import type React from 'react';

export const LegendType = {
  linearTop: 'linear-top',
  linearBottom: 'linear-bottom',
  tableRight: 'table-right',
  none: 'none',
} as const;
// eslint-disable-next-line @typescript-eslint/no-redeclare
export type LegendType = (typeof LegendType)[keyof typeof LegendType];

export type DatumId = string;

export type ItemDescriptor<Datum> = {
  id: DatumId;
  datum: Datum;
  index: number;
};

export type TooltipMode = 'idle' | 'hovering' | 'pinned';

export type AccessorFunction<Datum, R> = (datum: Datum) => R;
export type Accessor<Datum, R> = Extract<keyof Datum, string | number> | AccessorFunction<Datum, R>;

export type SelectedAccessorFunction<Datum> = (datum: Datum, index: number) => boolean;
export type SelectedAccessor<Datum> = Extract<keyof Datum, string | number> | SelectedAccessorFunction<Datum>;

export type ChartItemClickHandler<Datum> = (event: React.MouseEvent, item: ItemDescriptor<Datum>) => void;
export type ChartInteractionProps<Datum> = {
  onItemClick?: ChartItemClickHandler<Datum>;
  highlightedItem?: DatumId | null;
  onHighlightChange?: (item: ItemDescriptor<Datum> | null) => void;
  onTooltipClose?: () => void;
  pinOnClick?: boolean;
};

export type ChartAccessors<Datum> = {
  valueAccessor: Accessor<Datum, number>;
  idAccessor: Accessor<Datum, DatumId>;
  selectedAccessor?: SelectedAccessor<Datum>;
};

export type ChartAccessorFunctions<Datum> = {
  getValue: AccessorFunction<Datum, number>;
  getIdentifier: AccessorFunction<Datum, DatumId>;
  getSelected?: SelectedAccessorFunction<Datum>;
};

export type WithColorAccessor<Datum> = {
  colorAccessor: Accessor<Datum, string>;
};

export type WithColorAccessorFunctions<Datum> = {
  getColor: AccessorFunction<Datum, string>;
};

type FormatterContext = {
  where: 'data-label' | 'tooltip' | 'legend' | 'polyline-label';
};

export type Formatter<Datum> = (value: Datum, context: FormatterContext) => string | number;

export type LabelFormatter<Datum> = (value: Datum, context: FormatterContext) => React.ReactNode;

export type ChartFormatters<Datum> = {
  valueFormatter?: Formatter<Datum>;
  labelFormatter?: LabelFormatter<Datum>;
};

export type ChartSeries<Datum> = {
  data: Datum[];
  palette?: string[];
};

export type ChartDimensions = {
  width: number;
  height: number;
};

export const DATA_LABEL_MODE = {
  tag: 'tag',
  polyline: 'polyline',
} as const;

export type DataLabelMode = (typeof DATA_LABEL_MODE)[keyof typeof DATA_LABEL_MODE];

export type WithDataLabels = {
  dataLabelMode?: DataLabelMode;
};

export type WithLegend = {
  legendType?: LegendType;
};

export const PieVariant = {
  pie: 'pie',
  donut: 'donut',
} as const;
// eslint-disable-next-line @typescript-eslint/no-redeclare
export type PieVariant = (typeof PieVariant)[keyof typeof PieVariant];

export type WithPieVariant = {
  variant?: PieVariant;
};

export type LegendConfig = {
  position: 'top' | 'bottom' | 'left' | 'right';
  direction: 'horizontal' | 'vertical';
};

export type Margin = { top: number; bottom: number; left: number; right: number };
export type WithMargin = { margin: Margin };

export type ChartRootProps<Datum> = ChartAccessors<Datum> &
  ChartFormatters<Datum> &
  ChartInteractionProps<Datum> &
  WithColorAccessor<Datum>;
